import React, { Component } from "react";
import { addSeconds, subSeconds } from "date-fns";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { IStreamData } from "@formant/core/model/IStreamData";
import { Uuid } from "@formant/core/model/Uuid";
import { Fader } from "@formant/ui/component/common/Fader";
import { LoadingIndicator } from "@formant/ui/component/common/LoadingIndicator";

import { newVideoEnabled } from "../../../config";
import { appStore, observeStore, queryStore } from "../../../stores";
import { DeviceSelector } from "../../common/DeviceSelector";
import { DeviceTags } from "../../common/DeviceTags";

import "../../common/VideoStack";

import { IBaseModuleProps } from "../IBaseModuleProps";
import { Module } from "../Module";
import { ModuleFooter } from "../ModuleFooter";
import { pointsByDevice } from "../utils/pointsByDevice";
import { VideoPlaylist } from "../VideoPlaylist";
import * as styles from "./index.scss";

@observer
export class VideoModule extends Component<IBaseModuleProps> {
    @observable
    private selectedDeviceId?: Uuid;
    private data: IStreamData<"video">[] = [];

    private liveDelaySampleCount = 1;
    private liveDelayAvg = 8;
    private livenessBuffer = 3;

    @observable
    private muted = true;

    componentDidMount() {
        const { streamName } = this.props;
        appStore.addCustomMenuHandler(streamName, "sound-off", () => {
            this.muted = true;
        });
        appStore.addCustomMenuHandler(streamName, "sound-on", () => {
            this.muted = false;
        });

        appStore.addCustomMenuHandler(streamName, "describe", async () => {
            appStore.setSnackbar("Image description is not available.");
        });
    }

    public render() {
        const { filter, streamName, streamNames, hovering, fullscreen } =
            this.props;
        const { selectedDeviceId, onSelectDevice } = this;
        const { time, mode, speed } = observeStore;

        const timeShift =
            mode === "live"
                ? Math.floor(-(this.liveDelayAvg + this.livenessBuffer))
                : 0;
        // we look further back in time because some folks upload long videos
        // that may not have a start time within the last 30 seconds
        const queryStartTime = subSeconds(time, 120 - timeShift);
        // we don't need to look too far into the future though because start times
        // don't fall out of our awareness as easy as videos in the past
        const queryEndTime = addSeconds(time, 30 + timeShift);
        const queryData = queryStore.moduleQuery(
            filter,
            streamNames || streamName,
            "video",
            queryStartTime,
            queryEndTime,
        );

        const shiftedCurrentTime = addSeconds(time, timeShift);

        const loading = queryData === undefined;

        this.data = (queryData !== "too much data" && queryData) || this.data;
        const data = this.data;

        const datas = pointsByDevice(data);
        const active = datas.find(
            ({ deviceId, name }) =>
                selectedDeviceId === deviceId && name === streamName,
        );
        const deviceIds = data.map(_ => _.deviceId);
        let points = active?.points || [];

        const current = points.find(
            _ =>
                _.time <= shiftedCurrentTime &&
                shiftedCurrentTime <=
                    new Date(_.time.getTime() + _.value.duration),
        );

        const lastPoint = points[points.length - 1];
        if (lastPoint && mode === "live") {
            const timeDiffLastPointToTime =
                (time.getTime() - lastPoint.time.getTime()) / 1000;
            if (timeDiffLastPointToTime >= 0 && timeDiffLastPointToTime <= 15) {
                this.liveDelayAvg =
                    (this.liveDelayAvg * this.liveDelaySampleCount +
                        timeDiffLastPointToTime) /
                    (this.liveDelaySampleCount + 1);
                this.liveDelaySampleCount++;

                // start resampling sensitivity after we get a large number of
                // just in case liveness delay was temporary
                if (this.liveDelaySampleCount >= 1000) {
                    this.liveDelaySampleCount = 1;
                }
            }
        }

        // don't pass in too many videos from the past, we only need the last 10
        // seconds really in case they are scrubbing around at the seconds level
        // these videos get preloaded so we don't want to have too many of them
        // otherwise it can cause browser slowdown
        if (current) {
            points = points.filter(_ => _.time > subSeconds(current?.time, 10));
        }

        const pointsFormatted = points
            .map(x =>
                [
                    x.value.url,
                    x.value.duration !== 0 ? x.value.duration : 3000,
                    x.time.getTime(),
                ].join(","),
            )
            .join(";");

        return (
            <Module
                {...this.props}
                loading={loading}
                empty={points.length === 0}
                type="video"
                className={styles["video-module"]}
            >
                <div className={styles.main}>
                    <Fader visible={points.length > 0}>
                        <LoadingIndicator visible={true} />
                    </Fader>
                    <div className={styles.view}>
                        {newVideoEnabled ? (
                            // @ts-expect-error: video-stack is not builtin
                            <video-stack
                                name={streamName}
                                is-playing={mode === "play" || mode === "live"}
                                speed={speed}
                                time={shiftedCurrentTime.getTime()}
                                muted={this.muted}
                                className={
                                    fullscreen && styles["fullscreen-video"]
                                }
                                videos={pointsFormatted}
                            />
                        ) : (
                            <VideoPlaylist videos={points} />
                        )}
                    </div>
                </div>
                <ModuleFooter visible={hovering} floating={true}>
                    {current && selectedDeviceId && (
                        <DeviceTags
                            deviceId={selectedDeviceId}
                            tags={current.tags}
                            time={current.time}
                        />
                    )}
                    <DeviceSelector
                        selected={selectedDeviceId}
                        deviceIds={deviceIds}
                        onSelect={onSelectDevice}
                        fullscreen={fullscreen}
                    />
                </ModuleFooter>
            </Module>
        );
    }

    private onSelectDevice = (deviceId: Uuid) => {
        this.selectedDeviceId = deviceId;
    };
}
